import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Card, CardBody, Button } from "reactstrap";
import AnimatedMapSprite from "../Maps/logic/AnimimatedMapSprite";
import api from "../../api";

const CollectionContest = () => {
  const [collectionDetails, setCollectionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);
  const [showPopup, setShowPopup] = useState(false);
  const [popupColor, setPopupColor] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [capturedPokemonImg, setCapturedPokemonImg] = useState("");

  // Rewards configuration
  const boxOptions = ["shadow", "golden", "diamond"];
  const getRandomBox = () =>
    boxOptions[Math.floor(Math.random() * boxOptions.length)];
  const rewards = [
    {
      place: "1st",
      coins: "75,000",
      colorBox: getRandomBox(),
      ultraLures: 5,
      safe: "golden",
    },
    {
      place: "2nd",
      coins: "50,000",
      colorBox: getRandomBox(),
      ultraLures: 2,
      safe: "safe",
    },
    {
      place: "3rd",
      coins: "25,000",
      colorBox: getRandomBox(),
      ultraLures: 1,
      safe: "bronze",
    },
  ];

  // Fetch collection contest details on mount
  useEffect(() => {
    api
      .get("/v1/collection/details", { withCredentials: true })
      .then((res) => {
        // Assuming the response data is in res.data
        setCollectionDetails(res.data);
        setLoading(false);
      })
      .catch((err) => console.error(err));
  }, []);

  // Update current time every second for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now() / 1000);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const getTimeLeft = (endEpoch) => {
    const diff = Math.max(0, Math.floor(endEpoch - currentTime));
    const d = Math.floor(diff / (3600 * 24));
    const h = Math.floor((diff % (3600 * 24)) / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return diff > 0 ? `${d}d ${h}h ${m}m ${s}s` : "Expired";
  };

  // Handle Submit All button click
  const handleSubmitAll = () => {
    api
      .post("/v1/collection/submit-all", {}, { withCredentials: true })
      .then((res) => {
        const response = res.data;
        // Update "Your Submitted" count
        setCollectionDetails((prev) => ({
          ...prev,
          currentUserCollection:
            prev.currentUserCollection + response.submittedCount,
        }));
        // Prepare popup message (displaying money and coin rewards)
        setPopupColor("#1d222e");
        setPopupMessage(
          `Submitted ${response.submittedCount} Pokémon! You earned ${response.reward.coins} coins and ${response.reward.money} money.`,
        );
        // Optionally show a money image (adjust path as needed)
        setCapturedPokemonImg(
          `/sprites/${collectionDetails.currentContestPokemon.name}.png`,
        );
        setShowPopup(true);
        // Hide popup after 5 seconds
        setTimeout(() => setShowPopup(false), 5000);
      })
      .catch((err) => {
        setPopupColor("red");
        setCapturedPokemonImg(null);
        setPopupMessage(`You have no Pokémon to submit!`);
        // Optionally show a money image (adjust path as needed)
        setShowPopup(true);
        // Hide popup after 5 seconds
        setTimeout(() => setShowPopup(false), 5000);
      });
  };

  if (loading) {
    return (
      <Container fluid className="mt-4">
        <p>Loading collection contest details...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Card>
        <CardBody>
          <h4 className="mb-3">Collection Contest Details</h4>
          <p>
            <strong>Note:</strong> If you won last month’s collection contest,
            you are ineligible to participate in this month’s round. The new
            round starts at the beginning of each month.
          </p>
          <p>
            For every Pokémon you submit, you earn{" "}
            <img
              src="/items/coin.png"
              alt="coin"
              width="24"
              style={{ verticalAlign: "middle", marginRight: "5px" }}
            />
            5 and{" "}
            <img
              src="/items/money.png"
              alt="money"
              width="24"
              style={{ verticalAlign: "middle", marginRight: "5px" }}
            />{" "}
            5,000 - 7,000.
          </p>
          <hr />
          <h5>Current Contest</h5>
          <Row className="mb-3 text-center">
            {/* Contest Pokémon */}
            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "150px",
                }}
              >
                {collectionDetails.currentContestPokemon && (
                  <>
                    <img
                      src={collectionDetails.currentContestPokemon.pic}
                      alt={collectionDetails.currentContestPokemon.name}
                      width="150"
                      height="150"
                      style={{ marginBottom: "8px" }}
                    />
                    <div>{collectionDetails.currentContestPokemon.name}</div>
                  </>
                )}
              </div>
            </Col>
            {/* Rotating Map Image */}
            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "150px",
                }}
              >
                <AnimatedMapSprite
                  pokeName={
                    collectionDetails.currentContestPokemon
                      ? collectionDetails.currentContestPokemon.name
                      : ""
                  }
                  folder="collection"
                />
                <div style={{ marginTop: "8px" }}>Map Image</div>
              </div>
            </Col>
            {/* Contest Spawn Rate & Expiration */}
            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "150px",
                }}
              >
                <div>
                  Spawn Rate: {collectionDetails.currentContestSpawnRate}%
                </div>
                <div>
                  Expires in:{" "}
                  {getTimeLeft(collectionDetails.currentContestExpiration)}
                </div>
              </div>
            </Col>
            {/* User's Submission Count & Submit All Button */}
            <Col xs={12} sm={6} md={3}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "150px",
                }}
              >
                <div>Amount Submitted:</div>
                <div style={{ fontSize: "1.5em", fontWeight: "bold" }}>
                  {collectionDetails.currentUserCollection}
                </div>
                <Button
                  color="primary"
                  onClick={handleSubmitAll}
                  className="mt-2"
                >
                  Submit All
                </Button>
              </div>
            </Col>
          </Row>
          {/* Rewards Table */}
          <hr />
          <h5>Rewards</h5>
          <Table hover responsive className="text-center">
            <thead>
              <tr>
                <th>Place</th>
                <th>Coins</th>
                <th>Color Box</th>
                <th>Ultra Lures</th>
                <th>Safe</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((reward, idx) => (
                <tr key={idx}>
                  <td>{reward.place}</td>
                  <td>
                    <img
                      src="/items/coin.png"
                      alt="coin"
                      width="24"
                      style={{ verticalAlign: "middle", marginRight: "5px" }}
                    />
                    {reward.coins}
                  </td>
                  <td>
                    <img
                      src={`/items/${reward.colorBox}box.png`}
                      alt={`${reward.colorBox} box`}
                      width="50"
                      height="50"
                    />
                  </td>
                  <td>
                    <img
                      src="/items/ultralure.png"
                      alt="ultralure"
                      width="40"
                      style={{ verticalAlign: "middle", marginRight: "5px" }}
                    />
                    {reward.ultraLures}
                  </td>
                  <td>
                    <img
                      src={
                        reward.safe === "golden"
                          ? "/items/goldensafe.png"
                          : reward.safe === "safe"
                            ? "/items/safe.png"
                            : "/items/bronzesafe.png"
                      }
                      alt={`${reward.safe} safe`}
                      width="50"
                      height="50"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <hr />
          <h5>Top 5 Collection Leaders</h5>
          <Table hover responsive>
            <thead>
              <tr className="text-center">
                <th>Rank</th>
                <th>Avatar</th>
                <th>Username</th>
                <th>Collection Count</th>
              </tr>
            </thead>
            <tbody>
              {collectionDetails.topCollectionPlayers.map((player, idx) => (
                <tr key={idx} className="text-center">
                  <td>
                    {idx + 1}
                    {idx === 0
                      ? "st"
                      : idx === 1
                        ? "nd"
                        : idx === 2
                          ? "rd"
                          : "th"}
                  </td>
                  <td>
                    <img
                      src={`/avatars/${player.avatar}.png`}
                      alt={player.username}
                      width="50"
                      height="50"
                    />
                  </td>
                  <td>
                    <a
                      href={`playerprofile?user=${player.username}`}
                      style={{ textDecoration: "none", color: "white" }}
                    >
                      {player.username}
                    </a>
                  </td>
                  <td>{player.collection}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
      {/* Popup Modal */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "15px",
            background: popupColor,
            color: "white",
            zIndex: 9999,
            border: "2px solid white",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            textAlign: "center",
          }}
        >
          {capturedPokemonImg && (
            <img
              src={capturedPokemonImg}
              alt="Reward"
              style={{
                width: "60px",
                margin: "10px auto 0 auto",
                display: "block",
              }}
            />
          )}
          {popupMessage}
        </div>
      )}
    </Container>
  );
};

export default CollectionContest;
